import { ComponentType, ReactNode } from "react";
import { Link } from "react-router-dom";
import useStatistics from "datasets/hooks/useStatistics";
import Dataset from "datasets/types/interfaces/dataset";
import { getAccentColor } from "datasets/utils/color";
import { HardDrive, LucideIcon, Table2 } from "lucide-react";
import pluralize from "pluralize";
import prettyBytes from "pretty-bytes";
import { CardIllustration } from "~/assets/icons";
import { Skeleton } from "~/components/ui/skeleton";
import { cn } from "~/lib/utils";
import { nextGenDatasetDetailPath } from "~/router/constants";
import { AccessLevelBadge } from "./AccessLevelBadge";

interface StatProps {
  icon: LucideIcon | ComponentType<{ className?: string }>;
  children: ReactNode;
  loading?: boolean;
}

function Stat({ icon: Icon, children, loading }: StatProps) {
  return (
    <span className="flex items-center gap-1 text-xs font-medium">
      <Icon className="size-3.5" />
      {loading ? <Skeleton className="h-3 w-8" /> : children}
    </span>
  );
}

interface DatasetCardProps {
  dataset: Dataset;
}

export function DatasetCard({ dataset }: DatasetCardProps) {
  const { data: stats, isLoading } = useStatistics(dataset.id);
  const accentColor = getAccentColor(dataset.accentColor);

  return (
    <Link
      to={nextGenDatasetDetailPath(dataset.id)}
      data-testid="nextgen-dataset-card"
      className="group block h-full rounded-lg focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
    >
      <article
        className={cn(
          "relative flex h-full flex-col rounded-lg border border-border bg-card shadow-sm",
          "transition-colors group-hover:border-border/80 group-hover:bg-accent/40"
        )}
      >
        <div
          className="absolute inset-x-0 -top-px h-10 rounded-t-lg"
          style={{ backgroundColor: accentColor }}
          aria-hidden="true"
        />
        <div
          className={cn(
            "relative mt-[26px] flex flex-1 flex-col gap-3",
            "rounded-lg rounded-tl-none bg-card px-6 pb-3 pt-4"
          )}
        >
          <CardIllustration
            aria-hidden="true"
            className="pointer-events-none absolute -top-4 left-0 z-10 h-[29px] w-[95px] text-card"
          />
          <h3 className="line-clamp-1 text-base font-semibold leading-tight text-foreground">
            {dataset.title}
          </h3>
          <p className="line-clamp-4 flex-1 text-xs leading-snug text-muted-foreground">
            {dataset.description}
          </p>
          <div className="flex items-center justify-between gap-2">
            <AccessLevelBadge accessLevel={dataset.access_level} />
            <div className="flex shrink-0 items-center gap-3 text-muted-foreground/60">
              <Stat icon={Table2}>
                {`${dataset.table_count} ${pluralize("Table", dataset.table_count)}`}
              </Stat>
              <Stat icon={HardDrive} loading={isLoading}>
                {prettyBytes(stats?.table_total_bytes ?? 0)}
              </Stat>
            </div>
          </div>
        </div>
      </article>
    </Link>
  );
}
